import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { connect } from 'react-redux';
import { createStripeConnectAccount, checkStripeConnectStatus, resendStripeConnectLink } from '../../../actions/stripeConnect.actions';
import { getStripeConnect } from '../../../selectors';

class StripeOnboardingWizard extends Component {
  state = {
    currentStep: 1,
    stripeAccountId: null,
    onboardingUrl: null,
    onboardingComplete: false,
    windowRef: null,
    checkingStatus: false,
    creatingAccount: false,
    email: ''
  };

  componentDidMount() {
    // If we have an existing account ID, skip account creation and go to verification (step 3)
    if (this.props.existingAccountId) {
      this.setState({
        stripeAccountId: this.props.existingAccountId,
        currentStep: 3
      });
      // toastr.info('Continuing from your existing Stripe Connect account...', { position: "top-center" });
    }
  }

  handleCreateAccount = async () => {
    const { email } = this.state;
    // might add later
    // if (!email || !email.includes('@')) {
    //   toastr.error('Please enter a valid email address', { position: "top-center" });
    //   return;
    // }

    this.setState({ creatingAccount: true });

    try {
      const response = await this.props.createStripeConnectAccount(email, 1);

      if (response && response.data && response.data.success) {
        const { stripeAccountId, onboardingUrl } = response.data;

        this.setState({
          stripeAccountId,
          onboardingUrl,
          currentStep: 2,
          creatingAccount: false
        });

        toastr.success('Stripe Connect account created successfully!', { position: "top-center" });
      } else {
        toastr.error('Failed to create Stripe Connect account', { position: "top-center" });
        this.setState({ creatingAccount: false });
      }
    } catch (error) {
      toastr.error('Failed to create Stripe Connect account', { position: "top-center" });
      this.setState({ creatingAccount: false });
    }
  };

  handleStartOnboarding = () => {
    if (this.state.onboardingUrl) {
      const windowRef = window.open(this.state.onboardingUrl, '_blank');

      this.setState({
        windowRef,
        currentStep: 3
      });
      this.checkWindowClosed();
    }
  };

  handleContinueOnboarding = async () => {
    const { email } = this.state;

    if (!email || !email.includes('@')) {
      toastr.error('Please enter a valid email address', { position: "top-center" });
      return;
    }

    try {
      const response = await this.props.resendStripeConnectLink(email, 1);

      if (response && response.data && response.data.success) {
        const { onboardingUrl } = response.data;

        const windowRef = window.open(onboardingUrl, '_blank');

        this.setState({
          windowRef,
          onboardingUrl,
          currentStep: 3
        });
        this.checkWindowClosed();

        toastr.success('Fresh onboarding link generated!', { position: "top-center" });
      } else {
        toastr.error('Failed to generate fresh onboarding link', { position: "top-center" });
      }
    } catch (error) {
      toastr.error('Failed to continue onboarding process', { position: "top-center" });
    }
  };

  checkWindowClosed = () => {
    const { windowRef } = this.state;

    if (windowRef && windowRef.closed) {
      this.setState({ currentStep: 3 });
      return;
    }

    if (windowRef) {
      setTimeout(this.checkWindowClosed, 1000);
    }
  };

  handleCheckStatus = async () => {
    if (!this.state.stripeAccountId) {
      toastr.error('No Stripe account ID found', { position: "top-center" });
      return;
    }

    this.setState({ checkingStatus: true });

    try {
      const response = await this.props.checkStripeConnectStatus(this.state.stripeAccountId);

      if (response && response.data && response.data.success) {
        const isComplete = response.data.onboarding_complete ||
                          response.data.onboardingComplete ||
                          response.data.charges_enabled ||
                          response.data.chargesEnabled ||
                          response.data.details_submitted ||
                          response.data.detailsSubmitted ||
                          response.data.payouts_enabled ||
                          response.data.payoutsEnabled ||
                          response.data.complete ||
                          response.data.status === 'complete' ||
                          response.data.status === 'COMPLETE' ||
                          false;

        if (isComplete) {
          this.setState({
            onboardingComplete: true,
            currentStep: 4
          });
          toastr.success('Onboarding completed successfully!', { position: "top-center" });

          this.props.onOnboardingComplete();
          this.props.onComplete();
        } else {
          toastr.warning('Onboarding is not yet complete. Please finish the setup process.', { position: "top-center" });
        }
      } else {
        toastr.error('Failed to check onboarding status', { position: "top-center" });
      }
    } catch (error) {
      toastr.error('Failed to check onboarding status', { position: "top-center" });
    } finally {
      this.setState({ checkingStatus: false });
    }
  };

  handleCheckStatusAndOpenOnboarding = async () => {
    if (!this.state.stripeAccountId) {
      toastr.error('No Stripe account ID found', { position: "top-center" });
      return;
    }

    this.setState({ checkingStatus: true });

    try {
      const response = await this.props.checkStripeConnectStatus(this.state.stripeAccountId);

      if (response && response.data && response.data.success) {
        const isComplete = response.data.onboarding_complete ||
                          response.data.onboardingComplete ||
                          response.data.charges_enabled ||
                          response.data.chargesEnabled ||
                          response.data.details_submitted ||
                          response.data.detailsSubmitted ||
                          response.data.payouts_enabled ||
                          response.data.payoutsEnabled ||
                          response.data.complete ||
                          response.data.status === 'complete' ||
                          response.data.status === 'COMPLETE' ||
                          false;

        if (isComplete) {
          this.setState({
            onboardingComplete: true,
            currentStep: 4,
            checkingStatus: false
          });
          toastr.success('Onboarding completed successfully!', { position: "top-center" });
          this.props.onOnboardingComplete();
          this.props.onComplete();
          return;
        }

        const onboardingUrl = response.data.onboardingUrl || response.data.onboarding_url;
        if (onboardingUrl) {
          const windowRef = window.open(onboardingUrl, '_blank');
          this.setState({
            windowRef,
            onboardingUrl,
            checkingStatus: false
          });
          this.checkWindowClosed();
        } else {
          toastr.warning('Onboarding is not yet complete. No onboarding link in response.', { position: "top-center" });
        }
      } else {
        toastr.error('Failed to check onboarding status', { position: "top-center" });
      }
    } catch (error) {
      toastr.error('Failed to check onboarding status', { position: "top-center" });
    } finally {
      this.setState({ checkingStatus: false });
    }
  };

  renderStep1 = () => (
    <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
          Welcome to the Camvio + Stripe Connected Account Setup
        </h4>
        <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
          Here you can create a Stripe account and connect it to Camvio.
        </p>
        <p style={{ color: '#6c757d', fontSize: '0.9rem', marginBottom: 0 }}>
          There are 4 simple steps to complete your setup:
        </p>
      </div>

      <div className="mb-4">
        <img src="/images/Stripe.svg" alt="Stripe" style={{ maxWidth: "200px", maxHeight: "80px" }} />
      </div>

      <div style={{ textAlign: 'left', maxWidth: '400px', margin: '0 auto' }} className="mb-4">
        <div style={{ marginBottom: '1rem' }}>
          <h6 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
            <span style={{
              display: 'inline-block',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#635BFF',
              color: 'white',
              textAlign: 'center',
              lineHeight: '24px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginRight: '10px'
            }}>1</span>
            Welcome & Overview
          </h6>
          <p style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '34px', marginBottom: 0 }}>
            Learn about the setup process
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h6 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
            <span style={{
              display: 'inline-block',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#635BFF',
              color: 'white',
              textAlign: 'center',
              lineHeight: '24px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginRight: '10px'
            }}>2</span>
            Create Stripe Account
          </h6>
          <p style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '34px', marginBottom: 0 }}>
            Generate your connected account
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h6 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
            <span style={{
              display: 'inline-block',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#635BFF',
              color: 'white',
              textAlign: 'center',
              lineHeight: '24px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginRight: '10px'
            }}>3</span>
            Verify Setup
          </h6>
          <p style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '34px', marginBottom: 0 }}>
            Confirm your account is ready for payments
          </p>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <h6 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>
            <span style={{
              display: 'inline-block',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: '#635BFF',
              color: 'white',
              textAlign: 'center',
              lineHeight: '24px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginRight: '10px'
            }}>4</span>
            Setup Complete
          </h6>
          <p style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '34px', marginBottom: 0 }}>
            Start accepting payments through Camvio
          </p>
        </div>
      </div>

      {/*<div className="mb-3" style={{ maxWidth: '400px', margin: '0 auto' }}>*/}
      {/*  <input*/}
      {/*    type="email"*/}
      {/*    className="form-control"*/}
      {/*    placeholder="Enter your email address"*/}
      {/*    value={this.state.email}*/}
      {/*    onChange={(e) => this.setState({ email: e.target.value })}*/}
      {/*    required*/}
      {/*    style={{ padding: '10px', fontSize: '14px' }}*/}
      {/*  />*/}
      {/*</div>*/}

      <button
        onClick={this.handleCreateAccount}
        disabled={this.state.creatingAccount}
        style={{
          backgroundColor: '#635BFF',
          borderColor: '#635BFF',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '6px',
          border: 'none',
          fontSize: '16px',
          fontWeight: '600',
          cursor: this.state.creatingAccount ? 'wait' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: this.state.creatingAccount ? 0.8 : 1
        }}
        onMouseOver={(e) => !this.state.creatingAccount && (e.target.style.backgroundColor = '#5a52e8')}
        onMouseOut={(e) => !this.state.creatingAccount && (e.target.style.backgroundColor = '#635BFF')}
      >
        {this.state.creatingAccount ? (
          <>
            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
            Creating Account...
          </>
        ) : (
          'Create Stripe Connect Account'
        )}
      </button>
    </div>
  );

  renderStep2 = () => (
    <div className="wizard-step active">
      <div className="step-header">
        <h4 className="step-title">Account Created Successfully</h4>
        <p className="step-description">Your Stripe Connect account has been created. Now let's complete the setup.</p>
      </div>
      <div className="step-content text-center">
        <div className="mb-4">
          <i className="fas fa-check-circle" style={{ fontSize: '3rem', color: '#635BFF' }}></i>
        </div>
        <div className="mb-4">
          <p><strong>Account ID:</strong> <code>{this.state.stripeAccountId}</code></p>
        </div>
        <p className="mb-4">Click the button below to open the Stripe onboarding process in a new tab.</p>
        <button
          onClick={this.handleStartOnboarding}
          style={{
            backgroundColor: '#635BFF',
            borderColor: '#635BFF',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a52e8'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#635BFF'}
        >
          <i className="fas fa-external-link-alt mr-2"></i>
          Start Onboarding Process
        </button>
      </div>
    </div>
  );

  // renderStep3 = () => (
  //   <div className="wizard-step active">
  //     <div className="step-header">
  //       <h4 className="step-title">Complete Your Setup</h4>
  //       <p className="step-description">Please complete the onboarding process in the new tab that opened.</p>
  //     </div>
  //     <div className="step-content text-center">
  //       <div className="mb-4">
  //         <i className="fas fa-external-link-alt text-primary" style={{ fontSize: '3rem' }}></i>
  //       </div>
  //       <div className="loading-indicator mb-4">
  //         <div className="spinner-border text-primary" role="status">
  //           <span className="sr-only">Loading...</span>
  //         </div>
  //         <p className="mt-2">Waiting for onboarding completion...</p>
  //       </div>
  //       <p className="mb-4">
  //         Complete the required information in the Stripe onboarding tab, then come back here to verify your setup.
  //       </p>
  //       <button
  //         className="btn btn-secondary"
  //         onClick={() => this.setState({ currentStep: 4 })}
  //       >
  //         I've Completed Onboarding
  //       </button>
  //     </div>
  //   </div>
  // );

  renderStep3 = () => (
    <div className="wizard-step active">
      <div className="step-header">
        <h4 className="step-title">
          {this.props.existingAccountId ? 'Continue Setup' : 'Verify Setup'}
        </h4>
        <p className="step-description">
          {this.props.existingAccountId
            ? "Your Stripe Connect account exists but needs to complete onboarding."
            : "Let's verify that your Stripe Connect setup is complete."
          }
        </p>
      </div>
      <div className="step-content text-center">
        <div className="mb-4">
          <i className="fas fa-shield-alt text-warning" style={{ fontSize: '3rem' }}></i>
        </div>

        {this.props.existingAccountId ? (
          <>
            <p className="mb-3">
              Your account was created but the onboarding process needs to be completed.
            </p>
            {/*<div className="mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>*/}
            {/*  <input*/}
            {/*    type="email"*/}
            {/*    className="form-control"*/}
            {/*    placeholder="Enter your email address"*/}
            {/*    value={this.state.email}*/}
            {/*    onChange={(e) => this.setState({ email: e.target.value })}*/}
            {/*    required*/}
            {/*    style={{ padding: '10px', fontSize: '14px' }}*/}
            {/*  />*/}
            {/*</div>*/}
          </>
        ) : (
          <p className="mb-4">
            Click the button below to check if your Stripe Connect onboarding has been completed successfully.
          </p>
        )}

        <button
          onClick={this.handleCheckStatus}
          disabled={this.state.checkingStatus}
          style={{
            backgroundColor: '#635BFF',
            borderColor: '#635BFF',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            cursor: this.state.checkingStatus ? 'wait' : 'pointer',
            transition: 'all 0.2s ease',
            opacity: this.state.checkingStatus ? 0.6 : 1
          }}
          onMouseOver={(e) => !this.state.checkingStatus && (e.target.style.backgroundColor = '#5a52e8')}
          onMouseOut={(e) => !this.state.checkingStatus && (e.target.style.backgroundColor = '#635BFF')}
        >
          <i className="fas fa-search mr-2"></i>
          Check Current Status
        </button>

        {/* Simulate Success / Simulate Completed After Refresh - removed for now */}
        {/*<div className="mt-4">*/}
        {/*  <div className="btn-group mb-3" role="group">*/}
        {/*    <button*/}
        {/*      type="button"*/}
        {/*      className="btn btn-success btn-sm"*/}
        {/*      onClick={() => {*/}
        {/*        this.setState({ onboardingComplete: true, currentStep: 4 });*/}
        {/*        this.props.onComplete();*/}
        {/*      }}*/}
        {/*    >*/}
        {/*      Simulate Success*/}
        {/*    </button>*/}
        {/*    <button*/}
        {/*      type="button"*/}
        {/*      className="btn btn-info btn-sm"*/}
        {/*      onClick={() => this.props.onOnboardingComplete()}*/}
        {/*    >*/}
        {/*      Simulate Completed After Refresh*/}
        {/*    </button>*/}
        {/*  </div>*/}
        {/*</div>*/}

        <div className="mt-3">
          <button
            type="button"
            className="btn btn-link"
            onClick={this.handleCheckStatusAndOpenOnboarding}
            disabled={this.state.checkingStatus}
          >
            {this.state.checkingStatus ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                Checking...
              </>
            ) : this.props.existingAccountId ? (
              'Continue Onboarding Process'
            ) : (
              'Re-open Onboarding (if needed)'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  renderStep4 = () => (
    <div className="wizard-step active">
      <div className="step-header">
        <h4 className="step-title">Setup Complete!</h4>
        <p className="step-description">Your Stripe Connect account is ready to accept payments.</p>
      </div>
      <div className="step-content text-center">
        <div className="mb-4">
          <i className="fas fa-check-circle" style={{ fontSize: '3rem', color: '#635BFF' }}></i>
        </div>
        <div className="success-message mb-4">
          <h5 style={{ color: '#635BFF' }}>Congratulations!</h5>
          <p>Your Stripe Connect integration is now active and ready to process payments.</p>
        </div>
        <button
          className="btn btn-success btn-lg text-white"
          onClick={this.props.onComplete}
          style={{ backgroundColor: '#635BFF', borderColor: '#635BFF' }}
        >
          <i className="fas fa-arrow-right mr-2 text-white"></i>
          Continue to Configuration
        </button>
      </div>
    </div>
  );

  renderProgressBar = () => {
    const { currentStep } = this.state;
    const isExistingAccount = this.props.existingAccountId;

    const steps = [
      {
        number: 1,
        title: 'Welcome',
        active: currentStep >= 1,
        completed: currentStep > 1 || (isExistingAccount && currentStep >= 3)
      },
      {
        number: 2,
        title: 'Create Account',
        active: currentStep >= 2,
        completed: currentStep > 2 || (isExistingAccount && currentStep >= 3)
      },
      // {
      //   number: 3,
      //   title: 'Onboarding',
      //   active: currentStep >= 3,
      //   completed: currentStep > 3 || (isExistingAccount && currentStep >= 4)
      // },
      {
        number: 3,
        title: 'Verification',
        active: currentStep >= 3,
        completed: currentStep > 3
      },
      {
        number: 4,
        title: 'Complete',
        active: currentStep >= 4,
        completed: currentStep > 4
      }
    ];

    return (
      <div className="wizard-progress mb-4">
        <div className="progress-steps d-flex justify-content-between" style={{ position: 'relative' }}>
          {steps.map((step, index) => (
            <div key={step.number} style={{ textAlign: 'center', flex: 1, position: 'relative' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: step.completed ? '#635BFF' : (step.active ? '#635BFF' : '#e9ecef'),
                color: step.completed || step.active ? 'white' : '#6c757d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 0.5rem',
                fontWeight: 'bold',
                transition: 'all 0.3s ease'
              }}>
                {step.completed ? <i className="fas fa-check"></i> : step.number}
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: step.active ? '#635BFF' : '#6c757d',
                fontWeight: step.active ? '600' : 'normal'
              }}>
                {step.title}
              </div>
              {index < steps.length - 1 && (
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  left: '50%',
                  right: '-50%',
                  height: '2px',
                  background: step.completed ? '#635BFF' : '#e9ecef',
                  zIndex: -1
                }}></div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  render() {
    const { currentStep } = this.state;

    return (
      <div className="stripe-onboarding-wizard" style={{
        background: 'white',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div className="wizard-container">
          {this.renderProgressBar()}

          <div className="wizard-content">
            {currentStep === 1 && this.renderStep1()}
            {currentStep === 2 && this.renderStep2()}
            {/*{currentStep === 3 && this.renderStep3()}*/}
            {currentStep === 3 && this.renderStep3()}
            {currentStep === 4 && this.renderStep4()}
          </div>
        </div>
      </div>
    );
  }
}

StripeOnboardingWizard.propTypes = {
  createStripeConnectAccount: PropTypes.func.isRequired,
  checkStripeConnectStatus: PropTypes.func.isRequired,
  resendStripeConnectLink: PropTypes.func.isRequired,
  stripeConnect: PropTypes.object,
  onComplete: PropTypes.func.isRequired,
  onOnboardingComplete: PropTypes.func.isRequired,
  existingAccountId: PropTypes.string, // Account ID from previous session
};

const mapStateToProps = (state) => ({
  stripeConnect: getStripeConnect(state),
});

const mapDispatchToProps = {
  createStripeConnectAccount,
  checkStripeConnectStatus,
  resendStripeConnectLink,
};

export default connect(mapStateToProps, mapDispatchToProps)(StripeOnboardingWizard);
